import {
  CompanySettings,
  Employee,
  Lead,
  Material,
  ProCustomer,
  ProInvoice,
  ProJob,
  Quote,
  TimeEntry,
} from '../types/pro'
import { addDays, toISODate } from '../utils/pro'

/**
 * Realistic Danish demo data for the Pro workspace. Dates are generated
 * relative to today so the dashboard and calendar always look current.
 */

const today = new Date()
const day = (offset: number) => toISODate(addDays(today, offset))
const at = (offset: number, hour: number, minute = 0) => {
  const date = addDays(today, offset)
  date.setHours(hour, minute, 0, 0)
  return date.toISOString()
}

export const seedCompany: CompanySettings = {
  name: "LYKKE's EL",
  cvr: '38 45 12 07',
  address: 'Havnevej 22',
  postalCode: '8500',
  city: 'Grenaa',
  phone: '86 32 41 90',
  email: 'kontakt@lykke-el.dk',
  website: 'www.lykke-el.dk',
  hourlyRate: 545,
  vatRate: 0.25,
  paymentTermsDays: 14,
  quoteValidDays: 30,
  quoteFooter:
    'Tilbuddet er baseret på besigtigelse og gældende materialepriser. Eventuelt uforudset arbejde aftales inden udførelse.',
  invoiceFooter:
    'Betaling til reg. 9570 konto 1204 5583. Ved for sen betaling pålægges rente jf. renteloven.',
}

export const seedEmployees: Employee[] = [
  {
    name: 'Daniel Lykke',
    role: 'Administrator',
    phone: '20 41 78 33',
    email: 'daniel@lykke-el.dk',
    color: '#EA580C',
    hourlyRate: 545,
    active: true,
  },
]

export const seedCustomers: ProCustomer[] = [
  {
    customerNo: 'K-1001',
    name: 'Jens Hansen',
    address: 'Skovvej 14',
    postalCode: '8500',
    city: 'Grenaa',
    phone: '28 45 67 91',
    email: 'jens@example.dk',
    notes: 'Har carport med plads til ladeboks på nordvæg. Hund i haven.',
    createdDate: day(-210),
    leadSource: 'Anbefaling',
  },
  {
    customerNo: 'K-1002',
    name: 'Bygma Auning A/S',
    contactPerson: 'Lene Bech',
    address: 'Industrivej 8',
    postalCode: '8963',
    city: 'Auning',
    phone: '87 71 22 40',
    email: 'lene.bech@bygma.dk',
    notes: 'Erhvervskunde. Faktura sendes til bogholderi, 30 dages betaling.',
    createdDate: day(-165),
    leadSource: 'Hjemmeside',
  },
  {
    customerNo: 'K-1003',
    name: 'Mette Kirkegaard',
    address: 'Nørregade 41, 2. th',
    postalCode: '8900',
    city: 'Randers C',
    phone: '22 78 39 04',
    email: 'mette.k@example.dk',
    notes: 'Lejlighed, adgang via bagtrappe. Nøgle hos vicevært.',
    createdDate: day(-98),
    leadSource: 'Google',
  },
  {
    customerNo: 'K-1004',
    name: 'Grenaa Idrætscenter',
    contactPerson: 'Poul Erik Sund',
    address: 'Ydesvej 2',
    postalCode: '8500',
    city: 'Grenaa',
    phone: '86 32 09 15',
    email: 'drift@grenaaidraetscenter.dk',
    notes: 'Arbejde skal udføres uden for åbningstid. Kontakt Poul Erik først.',
    createdDate: day(-74),
    leadSource: 'Anbefaling',
  },
  {
    customerNo: 'K-1005',
    name: 'Søren Dalsgaard',
    address: 'Møllebakken 7',
    postalCode: '8961',
    city: 'Allingåbro',
    phone: '40 16 55 28',
    email: 'soeren.d@example.dk',
    notes: 'Nybyg. Tavle skal sidde i bryggers.',
    createdDate: day(-41),
    leadSource: 'Facebook',
  },
  {
    customerNo: 'K-1006',
    name: 'Anne-Marie Thomsen',
    address: 'Havbakken 19',
    postalCode: '8500',
    city: 'Grenaa',
    phone: '26 03 71 88',
    email: 'am.thomsen@example.dk',
    notes: 'Ældre installation fra 1974. Bør gennemgås samlet.',
    createdDate: day(-22),
    leadSource: 'Telefon',
  },
]

export const seedMaterials: Material[] = [
  {
    name: '5G6 installationskabel',
    itemNo: 'KAB-5G6',
    unit: 'm',
    stock: 240,
    costPrice: 34.5,
    salesPrice: 52,
    category: 'Kabler',
  },
  {
    name: '5G2,5 installationskabel',
    itemNo: 'KAB-5G25',
    unit: 'm',
    stock: 410,
    costPrice: 18.9,
    salesPrice: 29,
    category: 'Kabler',
  },
  {
    name: 'RCBO 16A type B',
    itemNo: 'AUT-RCBO16',
    unit: 'stk',
    stock: 34,
    costPrice: 289,
    salesPrice: 445,
    category: 'Automatsikringer',
  },
  {
    name: 'RCBO 13A type B',
    itemNo: 'AUT-RCBO13',
    unit: 'stk',
    stock: 21,
    costPrice: 275,
    salesPrice: 425,
    category: 'Automatsikringer',
  },
  {
    name: 'Stikkontakt m/jord, hvid',
    itemNo: 'STK-1J',
    unit: 'stk',
    stock: 96,
    costPrice: 42,
    salesPrice: 79,
    category: 'Afbrydermateriel',
  },
  {
    name: 'Installationsrør 20 mm',
    itemNo: 'ROR-20',
    unit: 'm',
    stock: 180,
    costPrice: 9.4,
    salesPrice: 18,
    category: 'Rør og kanaler',
  },
  {
    name: 'Gruppetavle 12 mod.',
    itemNo: 'TAV-12M',
    unit: 'stk',
    stock: 6,
    costPrice: 1240,
    salesPrice: 1895,
    category: 'Tavler',
  },
  {
    name: 'Ladeboks Zaptec Go 22 kW',
    itemNo: 'LAD-ZAPGO',
    unit: 'stk',
    stock: 4,
    costPrice: 5450,
    salesPrice: 7995,
    category: 'Ladebokse',
  },
  {
    name: 'LED panel 600x600 36W',
    itemNo: 'BEL-P600',
    unit: 'stk',
    stock: 28,
    costPrice: 210,
    salesPrice: 349,
    category: 'Belysning',
  },
  {
    name: 'Småmaterialer',
    itemNo: 'DIV-SMAA',
    unit: 'sæt',
    stock: 999,
    costPrice: 85,
    salesPrice: 165,
    category: 'Diverse',
  },
]

export const seedLeads: Lead[] = [
  {
    name: 'Kasper Nybo',
    phone: '30 21 84 07',
    email: 'kasper.nybo@example.dk',
    address: 'Enghavevej 33',
    city: 'Grenaa',
    jobType: 'Installation af ladeboks',
    description:
      'Har fået ny elbil leveret om tre uger. Ønsker ladeboks på gavl mod indkørsel. Tavle er fra 2019.',
    source: 'Google',
    estimatedValue: 14500,
    ownerId: '',
    stage: 'nyt',
    notes: '',
    createdDate: day(-1),
    lastActivityDate: day(-1),
    activity: [
      {
        id: 'a1',
        date: day(-1),
        text: 'Lead oprettet fra kontaktformular via Google-annonce.',
        author: 'System',
      },
    ],
  },
  {
    name: 'Helle Brandt',
    phone: '25 47 19 63',
    email: 'helle.brandt@example.dk',
    address: 'Rougsøvej 112',
    city: 'Ørsted',
    jobType: 'Udskiftning af eltavle',
    description:
      'Gammel tavle med skruesikringer. HFI mangler helt. Vil have et samlet tilbud.',
    source: 'Anbefaling',
    estimatedValue: 24000,
    ownerId: '',
    stage: 'kontaktet',
    notes: 'Anbefalet af Jens Hansen. Ringes igen efter kl. 16.',
    createdDate: day(-4),
    lastActivityDate: day(-2),
    activity: [
      {
        id: 'a1',
        date: day(-4),
        text: 'Lead oprettet — ringede ind om formiddagen.',
        author: 'Daniel Lykke',
      },
      {
        id: 'a2',
        date: day(-2),
        text: 'Talt med Helle. Aftalt besigtigelse i næste uge.',
        author: 'Daniel Lykke',
      },
    ],
  },
  {
    name: 'Djurs Autolakering',
    phone: '86 30 55 12',
    email: 'kontor@djursautolak.dk',
    address: 'Bredstrupvej 9',
    city: 'Grenaa',
    jobType: 'LED-belysning i værksted',
    description:
      'Udskiftning af 24 lysstofarmaturer til LED-paneler i værkstedshal.',
    source: 'Telefon',
    estimatedValue: 38000,
    ownerId: '',
    stage: 'besigtigelse',
    notes: 'Besigtiget. Skal bruge lift til de høje armaturer — regn med en dag ekstra.',
    createdDate: day(-11),
    lastActivityDate: day(-3),
    activity: [
      {
        id: 'a1',
        date: day(-11),
        text: 'Ringede ind efter anbefaling fra nabovirksomhed.',
        author: 'Mikkel Vestergaard',
      },
      {
        id: 'a2',
        date: day(-3),
        text: 'Besigtigelse gennemført. Målt op og fotograferet.',
        author: 'Mikkel Vestergaard',
      },
    ],
  },
  {
    name: 'Lars Overgaard',
    phone: '61 28 40 75',
    email: 'lars.overgaard@example.dk',
    address: 'Fuglsangsvej 4',
    city: 'Auning',
    jobType: 'Stikkontakter i tilbygning',
    description: 'Ny tilbygning på 28 m². 9 stikkontakter, 3 udtag til loftlampe.',
    source: 'Facebook',
    estimatedValue: 11200,
    ownerId: '',
    stage: 'tilbud-skal-laves',
    notes: 'Tegning modtaget på mail. Skal have tilbud senest fredag.',
    createdDate: day(-9),
    lastActivityDate: day(-5),
    activity: [
      {
        id: 'a1',
        date: day(-9),
        text: 'Skrev via Facebook. Sendte tegning samme dag.',
        author: 'Daniel Lykke',
      },
    ],
  },
  {
    name: 'Marianne Skov',
    phone: '27 66 91 30',
    email: 'm.skov@example.dk',
    address: 'Strandgården 21',
    city: 'Bønnerup Strand',
    jobType: 'Fejlfinding på belysning',
    description:
      'Udendørs belysning slår HFI ud i regnvejr. Sommerhus, adgang efter aftale.',
    source: 'Hjemmeside',
    estimatedValue: 6500,
    ownerId: '',
    stage: 'tilbud-sendt',
    notes: 'Tilbud sendt. Afventer svar — rykkes på mandag hvis intet.',
    createdDate: day(-16),
    lastActivityDate: day(-6),
    activity: [
      {
        id: 'a1',
        date: day(-16),
        text: 'Henvendelse via hjemmesidens formular.',
        author: 'System',
      },
      {
        id: 'a2',
        date: day(-6),
        text: 'Tilbud T-2026-0004 sendt på mail.',
        author: 'Daniel Lykke',
      },
    ],
  },
  {
    name: 'Anne-Marie Thomsen',
    phone: '26 03 71 88',
    email: 'am.thomsen@example.dk',
    address: 'Havbakken 19',
    city: 'Grenaa',
    jobType: 'Gennemgang af installation',
    description: 'Hus fra 1974. Ønsker tilstandsrapport på el og udbedring.',
    source: 'Telefon',
    estimatedValue: 19500,
    ownerId: '',
    stage: 'vundet',
    notes: 'Konverteret til kunde K-1006.',
    createdDate: day(-30),
    lastActivityDate: day(-22),
    activity: [
      {
        id: 'a1',
        date: day(-30),
        text: 'Ringede ind efter avisomtale.',
        author: 'Daniel Lykke',
      },
      {
        id: 'a2',
        date: day(-22),
        text: 'Tilbud accepteret. Oprettet som kunde og opgave.',
        author: 'Daniel Lykke',
      },
    ],
  },
  {
    name: 'Torben Riis',
    phone: '23 90 14 58',
    email: 'torben.riis@example.dk',
    address: 'Kastanjealle 6',
    city: 'Randers NØ',
    jobType: 'Installation af varmepumpe-tilslutning',
    description: 'Skulle bruge tilslutning til luft-til-vand pumpe.',
    source: 'Instagram',
    estimatedValue: 8900,
    ownerId: '',
    stage: 'tabt',
    notes: 'Valgte leverandørens egen elektriker. Pris var afgørende.',
    createdDate: day(-25),
    lastActivityDate: day(-14),
    activity: [
      {
        id: 'a1',
        date: day(-14),
        text: 'Meldte tilbage at de går videre med anden leverandør.',
        author: 'Mikkel Vestergaard',
      },
    ],
  },
]

/** Jobs reference customers by index — resolved when seeding. */
export const seedJobs: (Omit<ProJob, 'customerId' | 'customerName'> & {
  customerIndex: number
})[] = [
  {
    customerIndex: 0,
    jobNo: 'O-2026-0041',
    address: 'Skovvej 14',
    city: 'Grenaa',
    title: 'Installation af ladeboks',
    description:
      'Montering af Zaptec Go på carportgavl, ny gruppe i tavle og 18 m 5G6 fra tavle til boks.',
    status: 'i-gang',
    start: at(0, 8, 0),
    durationHours: 4,
    employeeIds: [],
    estimatedHours: 5,
    materials: [
      {
        id: 'm1',
        name: 'Ladeboks Zaptec Go 22 kW',
        itemNo: 'LAD-ZAPGO',
        quantity: 1,
        costPrice: 5450,
        salesPrice: 7995,
      },
      {
        id: 'm2',
        name: '5G6 installationskabel',
        itemNo: 'KAB-5G6',
        quantity: 18,
        costPrice: 34.5,
        salesPrice: 52,
      },
      {
        id: 'm3',
        name: 'RCBO 16A type B',
        itemNo: 'AUT-RCBO16',
        quantity: 1,
        costPrice: 289,
        salesPrice: 445,
      },
    ],
    photos: [],
    documents: [],
    notes: 'Hund i haven — ring på inden du går om bagved.',
  },
  {
    customerIndex: 2,
    jobNo: 'O-2026-0042',
    address: 'Nørregade 41, 2. th',
    city: 'Randers C',
    title: 'Fejlfinding på belysning',
    description:
      'Loftlamper i stue og gang slukker sporadisk. Mistanke om dårlig samling i dåse.',
    status: 'planlagt',
    start: at(0, 13, 0),
    durationHours: 2,
    employeeIds: [],
    estimatedHours: 2,
    materials: [],
    photos: [],
    documents: [],
    notes: 'Nøgle hentes hos vicevært i nr. 39.',
  },
  {
    customerIndex: 4,
    jobNo: 'O-2026-0043',
    address: 'Møllebakken 7',
    city: 'Allingåbro',
    title: 'Udskiftning af eltavle',
    description:
      'Ny 12-moduls gruppetavle i bryggers, flytning af 8 grupper og montering af RCBO på alle.',
    status: 'planlagt',
    start: at(1, 7, 30),
    durationHours: 7,
    employeeIds: [],
    estimatedHours: 8,
    materials: [
      {
        id: 'm1',
        name: 'Gruppetavle 12 mod.',
        itemNo: 'TAV-12M',
        quantity: 1,
        costPrice: 1240,
        salesPrice: 1895,
      },
      {
        id: 'm2',
        name: 'RCBO 13A type B',
        itemNo: 'AUT-RCBO13',
        quantity: 8,
        costPrice: 275,
        salesPrice: 425,
      },
    ],
    photos: [],
    documents: [],
    notes: '',
  },
  {
    customerIndex: 1,
    jobNo: 'O-2026-0044',
    address: 'Industrivej 8',
    city: 'Auning',
    title: 'Installation af stikkontakter i lager',
    description: '14 nye dobbelte stikkontakter på lagerreoler, ført i kanal.',
    status: 'planlagt',
    start: at(2, 8, 0),
    durationHours: 6,
    employeeIds: [],
    estimatedHours: 6,
    materials: [
      {
        id: 'm1',
        name: 'Stikkontakt m/jord, hvid',
        itemNo: 'STK-1J',
        quantity: 14,
        costPrice: 42,
        salesPrice: 79,
      },
      {
        id: 'm2',
        name: 'Installationsrør 20 mm',
        itemNo: 'ROR-20',
        quantity: 46,
        costPrice: 9.4,
        salesPrice: 18,
      },
    ],
    photos: [],
    documents: [],
    notes: 'Adgang gennem port 2. Meld ankomst i receptionen.',
  },
  {
    customerIndex: 3,
    jobNo: 'O-2026-0045',
    address: 'Ydesvej 2',
    city: 'Grenaa',
    title: 'LED-belysning i omklædning',
    description: 'Udskiftning af 18 armaturer til LED-paneler.',
    status: 'afventer',
    start: at(3, 16, 0),
    durationHours: 5,
    employeeIds: [],
    estimatedHours: 5,
    materials: [
      {
        id: 'm1',
        name: 'LED panel 600x600 36W',
        itemNo: 'BEL-P600',
        quantity: 18,
        costPrice: 210,
        salesPrice: 349,
      },
    ],
    photos: [],
    documents: [],
    notes: 'Afventer at centret melder tilbage om aftentidspunkt.',
  },
  {
    customerIndex: 5,
    jobNo: 'O-2026-0040',
    address: 'Havbakken 19',
    city: 'Grenaa',
    title: 'Gennemgang af installation',
    description:
      'Samlet gennemgang af installation fra 1974 med udbedring af fejl i køkken og bad.',
    status: 'faerdig',
    start: at(-5, 8, 0),
    durationHours: 8,
    employeeIds: [],
    estimatedHours: 10,
    materials: [
      {
        id: 'm1',
        name: 'Småmaterialer',
        itemNo: 'DIV-SMAA',
        quantity: 3,
        costPrice: 85,
        salesPrice: 165,
      },
      {
        id: 'm2',
        name: '5G2,5 installationskabel',
        itemNo: 'KAB-5G25',
        quantity: 32,
        costPrice: 18.9,
        salesPrice: 29,
      },
    ],
    photos: [],
    documents: [],
    notes: '',
  },
  {
    customerIndex: 0,
    jobNo: 'O-2026-0038',
    address: 'Skovvej 14',
    city: 'Grenaa',
    title: 'Udskiftning af udendørs stikkontakter',
    description: 'To defekte udendørs stik udskiftet til IP44.',
    status: 'faktureret',
    start: at(-18, 9, 0),
    durationHours: 3,
    employeeIds: [],
    estimatedHours: 3,
    materials: [
      {
        id: 'm1',
        name: 'Stikkontakt m/jord, hvid',
        itemNo: 'STK-1J',
        quantity: 2,
        costPrice: 42,
        salesPrice: 79,
      },
    ],
    photos: [],
    documents: [],
    notes: '',
  },
  {
    customerIndex: 2,
    jobNo: 'O-2026-0046',
    address: 'Nørregade 41, 2. th',
    city: 'Randers C',
    title: 'Montering af emhætte-udtag',
    description: 'Nyt udtag bag emhætte i køkken.',
    status: 'ikke-planlagt',
    durationHours: 2,
    employeeIds: [],
    estimatedHours: 2,
    materials: [],
    photos: [],
    documents: [],
    notes: 'Kunden melder tilbage om dato når køkkenet er leveret.',
  },
]

export const seedQuotes: (Omit<Quote, 'customerId' | 'customerName'> & {
  customerIndex: number
})[] = [
  {
    customerIndex: 4,
    quoteNo: 'T-2026-0001',
    title: 'Udskiftning af eltavle',
    status: 'accepteret',
    createdDate: day(-20),
    validUntil: day(10),
    notes: '',
    lines: [
      {
        id: 'q1',
        description: 'Gruppetavle 12 mod. inkl. montering',
        quantity: 1,
        unit: 'stk',
        unitPrice: 1895,
        discount: 0,
      },
      {
        id: 'q2',
        description: 'RCBO 13A type B',
        quantity: 8,
        unit: 'stk',
        unitPrice: 425,
        discount: 0,
      },
      {
        id: 'q3',
        description: 'Arbejdsløn, autoriseret elektriker',
        quantity: 8,
        unit: 'timer',
        unitPrice: 545,
        discount: 0,
      },
    ],
  },
  {
    customerIndex: 1,
    quoteNo: 'T-2026-0002',
    title: 'Stikkontakter i lager',
    status: 'accepteret',
    createdDate: day(-15),
    validUntil: day(15),
    notes: 'Erhvervsrabat aftalt med Lene.',
    lines: [
      {
        id: 'q1',
        description: 'Stikkontakt m/jord, dobbelt',
        quantity: 14,
        unit: 'stk',
        unitPrice: 79,
        discount: 10,
      },
      {
        id: 'q2',
        description: 'Installationsrør 20 mm',
        quantity: 46,
        unit: 'm',
        unitPrice: 18,
        discount: 10,
      },
      {
        id: 'q3',
        description: 'Arbejdsløn',
        quantity: 6,
        unit: 'timer',
        unitPrice: 545,
        discount: 0,
      },
    ],
  },
  {
    customerIndex: 3,
    quoteNo: 'T-2026-0003',
    title: 'LED-belysning i omklædning',
    status: 'sendt',
    createdDate: day(-8),
    validUntil: day(22),
    notes: 'Arbejde udføres uden for åbningstid, aftentillæg indregnet.',
    lines: [
      {
        id: 'q1',
        description: 'LED panel 600x600 36W',
        quantity: 18,
        unit: 'stk',
        unitPrice: 349,
        discount: 0,
      },
      {
        id: 'q2',
        description: 'Arbejdsløn inkl. aftentillæg',
        quantity: 5,
        unit: 'timer',
        unitPrice: 675,
        discount: 0,
      },
      {
        id: 'q3',
        description: 'Bortskaffelse af gamle armaturer',
        quantity: 1,
        unit: 'stk',
        unitPrice: 850,
        discount: 0,
      },
    ],
  },
  {
    customerIndex: 2,
    quoteNo: 'T-2026-0004',
    title: 'Fejlfinding og udbedring, udendørs belysning',
    status: 'set',
    createdDate: day(-6),
    validUntil: day(24),
    notes: '',
    lines: [
      {
        id: 'q1',
        description: 'Fejlfinding, timepris',
        quantity: 4,
        unit: 'timer',
        unitPrice: 545,
        discount: 0,
      },
      {
        id: 'q2',
        description: 'Småmaterialer',
        quantity: 2,
        unit: 'sæt',
        unitPrice: 165,
        discount: 0,
      },
    ],
  },
  {
    customerIndex: 0,
    quoteNo: 'T-2026-0005',
    title: 'Ladeboks til carport',
    status: 'kladde',
    createdDate: day(-2),
    validUntil: day(28),
    notes: 'Mangler at bekræfte kabellængde med kunden.',
    lines: [
      {
        id: 'q1',
        description: 'Ladeboks Zaptec Go 22 kW',
        quantity: 1,
        unit: 'stk',
        unitPrice: 7995,
        discount: 0,
      },
      {
        id: 'q2',
        description: '5G6 installationskabel',
        quantity: 18,
        unit: 'm',
        unitPrice: 52,
        discount: 0,
      },
    ],
  },
  {
    customerIndex: 5,
    quoteNo: 'T-2025-0098',
    title: 'Gennemgang af installation',
    status: 'udloebet',
    createdDate: day(-75),
    validUntil: day(-45),
    notes: '',
    lines: [
      {
        id: 'q1',
        description: 'Gennemgang og tilstandsrapport',
        quantity: 6,
        unit: 'timer',
        unitPrice: 545,
        discount: 0,
      },
    ],
  },
]

export const seedInvoices: (Omit<ProInvoice, 'customerId' | 'customerName'> & {
  customerIndex: number
})[] = [
  {
    customerIndex: 0,
    invoiceNo: 'F-2026-0112',
    status: 'betalt',
    invoiceDate: day(-16),
    dueDate: day(-2),
    notes: '',
    lines: [
      {
        id: 'i1',
        description: 'Arbejdsløn',
        quantity: 3,
        unit: 'timer',
        unitPrice: 545,
      },
      {
        id: 'i2',
        description: 'Stikkontakt m/jord IP44',
        quantity: 2,
        unit: 'stk',
        unitPrice: 149,
      },
    ],
  },
  {
    customerIndex: 5,
    invoiceNo: 'F-2026-0113',
    status: 'sendt',
    invoiceDate: day(-4),
    dueDate: day(10),
    notes: '',
    lines: [
      {
        id: 'i1',
        description: 'Arbejdsløn, gennemgang og udbedring',
        quantity: 9.5,
        unit: 'timer',
        unitPrice: 545,
      },
      {
        id: 'i2',
        description: '5G2,5 installationskabel',
        quantity: 32,
        unit: 'm',
        unitPrice: 29,
      },
      {
        id: 'i3',
        description: 'Småmaterialer',
        quantity: 3,
        unit: 'sæt',
        unitPrice: 165,
      },
    ],
  },
  {
    customerIndex: 1,
    invoiceNo: 'F-2026-0108',
    status: 'forfalden',
    invoiceDate: day(-44),
    dueDate: day(-14),
    notes: 'Rykker 1 sendt.',
    lines: [
      {
        id: 'i1',
        description: 'Servicebesøg, lagerbelysning',
        quantity: 4,
        unit: 'timer',
        unitPrice: 545,
      },
    ],
  },
  {
    customerIndex: 3,
    invoiceNo: 'F-2026-0107',
    status: 'forfalden',
    invoiceDate: day(-50),
    dueDate: day(-20),
    notes: '',
    lines: [
      {
        id: 'i1',
        description: 'Udbedring af fejl i hovedtavle',
        quantity: 6,
        unit: 'timer',
        unitPrice: 545,
      },
      {
        id: 'i2',
        description: 'RCBO 16A type B',
        quantity: 3,
        unit: 'stk',
        unitPrice: 445,
      },
    ],
  },
  {
    customerIndex: 2,
    invoiceNo: 'F-2026-0114',
    status: 'kladde',
    invoiceDate: day(0),
    dueDate: day(14),
    notes: 'Afventer at opgaven afsluttes.',
    lines: [],
  },
]

/** Time entries reference jobs and employees by index. */
export const seedTimeEntries: (Omit<TimeEntry, 'jobId' | 'employeeId'> & {
  jobIndex: number
  employeeIndex: number
})[] = [
  {
    jobIndex: 0,
    employeeIndex: 0,
    workType: 'Montage',
    date: day(0),
    hours: 3.5,
    note: 'Kabelføring og montering af boks',
    billable: true,
  },
  {
    jobIndex: 0,
    employeeIndex: 2,
    workType: 'Montage',
    date: day(0),
    hours: 2,
    note: 'Assistance ved tavlearbejde',
    billable: true,
  },
  {
    jobIndex: 5,
    employeeIndex: 0,
    workType: 'Service',
    date: day(-5),
    hours: 8,
    note: 'Gennemgang af hele installationen',
    billable: true,
  },
  {
    jobIndex: 5,
    employeeIndex: 1,
    workType: 'Fejlfinding',
    date: day(-5),
    hours: 1.5,
    note: 'Fejl i køkkengruppe',
    billable: true,
  },
  {
    jobIndex: 6,
    employeeIndex: 2,
    workType: 'Montage',
    date: day(-18),
    hours: 3,
    note: '',
    billable: true,
  },
  {
    jobIndex: 1,
    employeeIndex: 1,
    workType: 'Kørsel',
    date: day(-1),
    hours: 0.75,
    note: 'Kørsel Grenaa–Randers',
    billable: false,
  },
  {
    jobIndex: 3,
    employeeIndex: 1,
    workType: 'Montage',
    date: day(-2),
    hours: 4,
    note: 'Opsætning af kanal',
    billable: true,
  },
]
